'use client';

/**
 * VendorCatalogCard — full-bleed vendor product catalog (canvas).
 *
 * Mode4-native parallel paint of the legacy `<VendorCollectionsWidget />`.
 *
 * Selection model — canvas reads the *active tab's* entityId (not
 * useViewportSelection.entityId). The tab pins the canvas to one
 * vendor; clicking a sub-entity (collection / product) inside the
 * canvas updates the graph selection store so the rail + copilot
 * react, but the canvas stays painted on the parent vendor's
 * catalog. Same model the legacy widget uses (entityId is a prop
 * passed in by the canvas dispatcher from tab.entityId).
 *
 * Drill-down: collections grid → click → products grid with
 * breadcrumb back to the collections grid. Mirrors legacy.
 *
 * Cards-everywhere doctrine: the catalog is NOT one monolith. The
 * outer card is an *orchestrator* (content_key="vendor_catalog");
 * each collection is its own card (content_key="collection"), and in
 * the drill-in grid each product is a card (content_key="product").
 * Products earn cardhood — they carry the add-to-RFQ affordance and
 * the in-cart state, which is screen-truth a leaf tree would silently
 * drop. The recursion mirrors the explorer's VendorListCard. All
 * cards ride ONE vendor_explore payload (React Query dedups on the
 * shared query_key), so cardhood costs registry entries, not network.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="vendor_catalog"
 * @see components/explorer/cards/vendors/list-card.tsx — sibling recursion
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Loader2, XCircle, Store, ChevronRight, Package, Grid2X2 } from 'lucide-react';
import { useMemo } from 'react';
import { useVendorExplore, useVendors } from '@/lib/api/hooks';
import { VendorGrid } from '@/components/vendor/vendor-card';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens';
import type { CollectionDetail, ProductSummary } from '@/lib/api/types';
import { ProductTile } from './product-tile';
import { CardGrid } from '../card-grid';
import { CardShell } from '../card-shell';

const UNCATEGORIZED_ID = 'uncategorized';

export function VendorCatalogCard() {
  // Canvas reads from the *tab*, not viewport selection — clicking a
  // product/collection inside the canvas must NOT change which vendor
  // the canvas is painting. (useViewportSelection.entityId flips on
  // graph clicks; the tab pins.)
  const activeTabId = useTabStore((s) => s.activeTabId);
  const tabs = useTabStore((s) => s.tabs);
  const updateTab = useTabStore((s) => s.updateTab);
  const openTab = useTabStore((s) => s.openTab);
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const vendorId = activeTab?.type === 'vendor' ? (activeTab.entityId ?? '') : '';
  const vendorName = activeTab?.title ?? '';
  const selectedCollectionId = activeTab?.selectedCollectionId ?? null;

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const exploreStrategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'vendor_explore'),
  );

  // No-selection state paints the vendor directory (the wall). Same list the
  // explorer tree uses; a card click fires the same selectNode('vendor') the
  // tree does, so both entry points drive one state change → this card repaints
  // with the picked vendor's catalog.
  const vendorListStrategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'vendor_list'),
  );
  const { data: vendorListData } = useVendors(vendorListStrategy ?? '', { limit: 100 });

  const enabled = !!vendorId && !!exploreStrategy;
  const { data, isLoading, error } = useVendorExplore(
    exploreStrategy ?? '',
    enabled ? { vendor_id: vendorId } : null,
  );
  const vendor = data?.vendor ?? null;

  // The shared payload key — orchestrator + every collection/product
  // card ride this one vendor_explore entry, so the envelope dedups to
  // a single payload. Matches useVendorExplore's key shape
  // (['vendor', strategy, vendor_id]).
  const exploreQueryKey = useMemo(
    () => ['vendor', exploreStrategy ?? '', vendorId],
    [exploreStrategy, vendorId],
  );

  // Orchestrator export — the catalog summary + current drill state.
  // Per-collection and per-product children register their own. Called
  // before the early returns per Rules of Hooks.
  useCardExport(useMemo(() => {
    const productCount = data?.metadata?.product_count ?? 0;
    return {
      label: vendorName || vendor?.name || 'Catalog',
      content_key: 'vendor_catalog',
      surface: 'canvas' as const,
      status: (!vendorId
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : 'loaded') as 'idle' | 'loading' | 'error' | 'loaded',
      query_key: enabled ? exploreQueryKey : null,
      text: vendor
        ? `${vendorName || vendor.name} — ${vendor.collections.length} collections · ${productCount} products`
        : 'Catalog',
      json: vendor
        ? {
            vendor_id: vendorId,
            name: vendorName || vendor.name,
            collection_count: vendor.collections.length,
            product_count: productCount,
          }
        : null,
      state: { drilled_into: selectedCollectionId },
    };
  }, [vendor, vendorName, vendorId, isLoading, error, enabled, exploreQueryKey, data, selectedCollectionId]));

  if (!vendorId) {
    const vendors = vendorListData?.vendors ?? [];
    return (
      <div className="h-full overflow-y-auto">
        <div className="flex items-center gap-2 px-4 pt-4 text-muted-foreground">
          <Store className="h-4 w-4 opacity-60" />
          <p className={text.body}>Select a vendor to browse its catalogue</p>
        </div>
        <VendorGrid
          vendors={vendors.map((v) => ({
            semantic_id: v.semantic_id,
            name: v.name,
            monogram: v.monogram,
            monogram_bg: v.monogram_bg,
            monogram_fg: v.monogram_fg,
            product_count: v.product_count,
            logo: v.logo ?? null,
          }))}
          onSelect={(sid) => {
            const v = vendors.find((x) => x.semantic_id === sid);
            // Identical to the explorer tree's handleOpenInCanvas: open/activate
            // the vendor tab (the canvas reads the TAB), reset the collection
            // drill, then set graph selection for the rail/copilot.
            const tabId = `vendor-${sid}`;
            openTab({
              id: tabId,
              type: 'vendor',
              title: v?.name ?? '',
              entityId: sid,
              selectedCollectionId: null,
            });
            updateTab(tabId, { selectedCollectionId: null });
            selectNode({ id: sid, label: v?.name ?? '', type: 'vendor' });
          }}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data?.vendor) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-destructive" style={{ fontSize: surfaceBase.canvas }}>
        <XCircle className="h-8 w-8 mb-2 opacity-50" />
        <p className={text.body}>Failed to load vendor data</p>
      </div>
    );
  }

  const vendorData = data.vendor;

  // Resolve the active drill-in (collection from API or pseudo
  // "uncategorized" collection synthesized from loose products).
  const selectedCollection: CollectionDetail | null =
    selectedCollectionId === UNCATEGORIZED_ID
      ? vendorData.uncategorized_products.length > 0
        ? {
            semantic_id: UNCATEGORIZED_ID,
            name: 'Uncategorized',
            product_count: vendorData.uncategorized_products.length,
            products: vendorData.uncategorized_products,
          }
        : null
      : selectedCollectionId
      ? vendorData.collections.find((c) => c.semantic_id === selectedCollectionId) ?? null
      : null;

  const setSelectedCollection = (id: string | null) => {
    updateTab(activeTabId!, { selectedCollectionId: id });
  };

  const handleProductClick = (product: ProductSummary, collectionName: string) => {
    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
      properties: {
        list_price: product.list_price,
        image_url: product.image_url,
        collection: collectionName,
        vendor: vendorData.name,
      },
    });
  };

  const handleCollectionClick = (col: CollectionDetail | null) => {
    if (!col) return;
    setSelectedCollection(col.semantic_id);
    selectNode({
      id: col.semantic_id,
      label: col.name,
      type: 'collection',
      properties: { vendor: vendorData.name, product_count: col.product_count },
    });
  };

  // Drill-in view — products of the selected collection. The collection
  // is a card (drilled_in: true) wrapping its product cards.
  if (selectedCollection) {
    return (
      <div className="h-full flex flex-col" style={{ fontSize: surfaceBase.canvas }}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
          <button
            onClick={() => setSelectedCollection(null)}
            className={cn(text.body, 'text-muted-foreground hover:text-foreground transition-colors')}
          >
            {vendorName || vendorData.name}
          </button>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className={text.title}>{selectedCollection.name}</span>
          <span className={cn(text.muted, 'ml-auto')}>
            {selectedCollection.product_count} products
          </span>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <CardPlacementProvider
            value={{ surface: 'canvas', slot_id: `collection:${selectedCollection.semantic_id}` }}
          >
            <DrilledCollection
              collection={selectedCollection}
              queryKey={exploreQueryKey}
              vendorId={vendorId}
              vendorName={vendorData.name}
              onProductClick={handleProductClick}
            />
          </CardPlacementProvider>
        </div>
      </div>
    );
  }

  // Top-level view — collections grid. Each tile is a card.
  return (
    <div className="h-full flex flex-col" style={{ fontSize: surfaceBase.canvas }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div>
          <h2 className={text.heading}>{vendorName || vendorData.name}</h2>
          <p className={text.muted}>
            {vendorData.collections.length} collections · {data.metadata?.product_count ?? 0} products
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <CardGrid>
          {vendorData.collections.map((col) => (
            <CardPlacementProvider
              key={col.semantic_id}
              value={{ surface: 'canvas', slot_id: `collection:${col.semantic_id}` }}
            >
              <CollectionTile
                collection={col}
                queryKey={exploreQueryKey}
                onClick={() => handleCollectionClick(col)}
              />
            </CardPlacementProvider>
          ))}

          {vendorData.uncategorized_products.length > 0 && (
            <CardPlacementProvider
              value={{ surface: 'canvas', slot_id: `collection:${UNCATEGORIZED_ID}` }}
            >
              <UncategorizedTile
                collection={{
                  semantic_id: UNCATEGORIZED_ID,
                  name: 'Uncategorized',
                  product_count: vendorData.uncategorized_products.length,
                  products: vendorData.uncategorized_products,
                }}
                queryKey={exploreQueryKey}
                onClick={() =>
                  handleCollectionClick({
                    semantic_id: UNCATEGORIZED_ID,
                    name: 'Uncategorized',
                    product_count: vendorData.uncategorized_products.length,
                    products: vendorData.uncategorized_products,
                  })
                }
              />
            </CardPlacementProvider>
          )}
        </CardGrid>
      </div>
    </div>
  );
}

/**
 * Register a collection card. Shared by the top-level tiles
 * (drilled_in: false) and the drill-in body (drilled_in: true) so the
 * 'collection' export shape stays in one place. surface + per-instance
 * slot_id come from the parent's CardPlacementProvider; query_key is
 * the shared vendor_explore key.
 */
function useCollectionExport(
  collection: CollectionDetail,
  queryKey: readonly unknown[],
  drilledIn: boolean,
) {
  useCardExport(useMemo(() => ({
    label: collection.name,
    content_key: 'collection',
    status: 'loaded' as const,
    query_key: queryKey,
    text: `${collection.name} — ${collection.product_count} product${collection.product_count !== 1 ? 's' : ''}`,
    json: {
      collection_id: collection.semantic_id,
      name: collection.name,
      product_count: collection.product_count,
    },
    state: { drilled_in: drilledIn },
  }), [collection, queryKey, drilledIn]));
}

function CollectionTile({
  collection,
  queryKey,
  onClick,
}: {
  collection: CollectionDetail;
  queryKey: readonly unknown[];
  onClick: () => void;
}) {
  useCollectionExport(collection, queryKey, false);
  const thumbnailUrl = collection.products.find((p) => p.image_url)?.image_url ?? null;

  return (
    <CardShell
      as="button"
      interactive
      onClick={onClick}
      className="group relative overflow-hidden text-left w-full"
    >
      <div className="aspect-square bg-muted relative">
        {thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbnailUrl}
            alt={collection.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Grid2X2 className="w-12 h-12 text-muted-foreground" />
          </div>
        )}
        {/* count badge removed — the footer carries the product count, no
            number over the graphic. */}
      </div>
      <div className="p-2">
        <h3 className={cn(text.title, 'truncate group-hover:text-primary transition-colors')}>
          {collection.name}
        </h3>
        <p className={text.muted}>
          {collection.product_count} product{collection.product_count !== 1 ? 's' : ''}
        </p>
      </div>
    </CardShell>
  );
}

function UncategorizedTile({
  collection,
  queryKey,
  onClick,
}: {
  collection: CollectionDetail;
  queryKey: readonly unknown[];
  onClick: () => void;
}) {
  useCollectionExport(collection, queryKey, false);

  return (
    <CardShell
      as="button"
      interactive
      onClick={onClick}
      className="group relative overflow-hidden text-left w-full"
    >
      <div className="aspect-square bg-muted flex items-center justify-center">
        <Package className="w-12 h-12 text-muted-foreground" />
      </div>
      <div className="p-3">
        <h3 className={cn(text.title, 'truncate')}>{collection.name}</h3>
        <p className={text.muted}>
          {collection.product_count} products
        </p>
      </div>
    </CardShell>
  );
}

/**
 * Drill-in body — the selected collection as a card (drilled_in: true)
 * whose paint is the product grid. Each product is its own card.
 */
function DrilledCollection({
  collection,
  queryKey,
  vendorId,
  vendorName,
  onProductClick,
}: {
  collection: CollectionDetail;
  queryKey: readonly unknown[];
  vendorId: string;
  vendorName: string;
  onProductClick: (product: ProductSummary, collectionName: string) => void;
}) {
  useCollectionExport(collection, queryKey, true);

  return (
    <CardGrid>
      {collection.products.map((p) => (
        <CardPlacementProvider
          key={p.semantic_id}
          value={{ surface: 'canvas', slot_id: `product:${p.semantic_id}` }}
        >
          <ProductTile
            product={p}
            vendorId={vendorId}
            vendorName={vendorName}
            queryKey={queryKey}
            collectionName={collection.name}
            onClick={() => onProductClick(p, collection.name)}
          />
        </CardPlacementProvider>
      ))}
    </CardGrid>
  );
}

